import React from 'react';
import {
    MonitorSmartphone,
    Database,
    Settings,
    AlertCircle
} from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    LineChart, Line, Dot, Cell
} from 'recharts';
import Layout from '../../Layout'
import { Link } from 'react-router-dom';

// --- MOCK DATA ---
const locationData = [
    { name: 'HCM - Lầu 1', value: 0 },
    { name: 'HCM - Lầu 2', value: 0 },
    { name: 'HCM - Lầu 3', value: 0 },
    { name: 'HCM - Lầu 4', value: 0 },
    { name: 'HCM - Lầu 5', value: 0 },
    { name: 'HN - Lầu 1', value: 0 },
    { name: 'HN - Lầu 3', value: 0 },
    { name: 'HN - Lầu 4', value: 0 },
    { name: 'HN - Lầu 5', value: 0 },
    { name: 'Phú Yên', value: 0 },
    { name: 'Thaco', value: 0 },
    { name: 'Thiso', value: 0 },
    { name: 'HCM', value: 0 },
    { name: '[Không xác định]', value: 2400000, fill: '#4fd1c5' },
];

const dateData = [
    { date: '01/2010', value: 0 },
    { date: '01/2021', value: 1200 },
    { date: '04/2022', value: 150 },
    { date: '11/2022', value: 100 },
    { date: '02/2023', value: 50 },
    { date: '04/2023', value: 0 },
    { date: '06/2023', value: 0 },
    { date: '08/2023', value: 200 },
    { date: '10/2023', value: 100 },
    { date: '06/2024', value: 0 },
    { date: '10/2024', value: 0 },
    { date: '03/2025', value: 0 },
    { date: '05/2025', value: 0 },
    { date: '07/2025', value: 0 },
    { date: '09/2025', value: 8500 },
    { date: '11/2025', value: 0 },
    { date: '01/2026', value: 0 },
    { date: '05/2026', value: 0 },
];

// DATA ĐÃ CẬP NHẬT CHO PHÒNG BAN QUẢN LÝ
const departmentData = [
    { name: 'CÔNG TY CỔ PHẦN KHOTHI', value: 202, fill: '#22c55e' },
    { name: 'CÔNG TY CỔ PHẦN KHOTHI > [GTV] Chi nhánh HCM KHOTHI', value: 120, fill: '#ff4d4f' },
    { name: '[GTV] Chi nhánh HCM KHOTHI > [GTV] Phòng Hành Chính Nhân Sự', value: 118, fill: '#8ba1c5' },
    { name: 'CÔNG TY CỔ PHẦN KHOTHI > Tổng Công ty Phân bón và Hóa chất Dầu Khí - CTCP', value: 0, fill: 'transparent' },
    { name: 'Tổng Công ty Phân bón và Hóa chất Dầu Khí - CTCP > Marketing & Thiết kế', value: 0, fill: 'transparent' },
    { name: 'CÔNG TY CỔ PHẦN KHOTHI > Ban Giám Đốc', value: 8, fill: '#8b5cf6' },
    { name: 'CÔNG TY CỔ PHẦN KHOTHI > [GTV] Chi nhánh ngành F&B KHOTHI', value: 0, fill: 'transparent' },
    { name: 'CÔNG TY CỔ PHẦN KHOTHI > PVH - PHÒNG MARKETING', value: 0, fill: 'transparent' },
];

const assetGroupData = [
    { name: '7,690,000', value: 2, fill: '#22c55e' },
    { name: '31,700,000', value: 1, fill: '#ef4444' },
    { name: '3,000,000', value: 11, fill: '#818cf8' },
    { name: '450,000', value: 2, fill: '#c084fc' },
    { name: '299,000', value: 3, fill: '#0ea5e9' },
    { name: '880,000', value: 1, fill: '#8b5cf6' },
    { name: '3,000,000 3,000,000', value: 1, fill: '#eab308' },
    { name: '[Không xác định]', value: 28, fill: '#a855f7' },
    { name: '748,000 14,500,000', value: 1, fill: '#f43f5e' },
    { name: '1,699,000', value: 1, fill: '#f97316' },
    { name: '12,000,000', value: 4, fill: '#64748b' },
];

// --- CUSTOM COMPONENTS CHO CHART PHÒNG BAN ---

// Tooltip viền xanh, chữ xanh
const CustomDeptTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        if (data.value === 0) return null; // Ẩn tooltip nếu giá trị = 0

        return (
            <div className="bg-green-50/90 dark:bg-gray-800 border border-green-400 p-3 rounded shadow-md relative z-50">
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Phòng ban quản lý : <span className="font-bold text-green-700 dark:text-green-500">{data.name}</span>
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                    Số lượng : <span className="font-bold text-green-700 dark:text-green-500">{data.value}</span>
                </p>
            </div>
        );
    }
    return null;
};

// Y-Axis Tick tự động xuống dòng
const CustomDeptYAxisTick = ({ x, y, payload }) => {
    const maxCharsPerLine = 30; // Ký tự tối đa 1 dòng
    const words = payload.value.split(' ');
    let lines = [];
    let currentLine = '';

    words.forEach(word => {
        if ((currentLine + word).length > maxCharsPerLine) {
            lines.push(currentLine.trim());
            currentLine = word + ' ';
        } else {
            currentLine += word + ' ';
        }
    });
    if (currentLine) lines.push(currentLine.trim());

    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={0}
                y={0}
                dy={-((lines.length - 1) * 7)}
                textAnchor="end"
                className="fill-gray-600 dark:fill-gray-400 text-[10px]"
            >
                {lines.map((line, index) => (
                    <tspan x={-10} dy={index === 0 ? 0 : 14} key={index}>
                        {line}
                    </tspan>
                ))}
            </text>
        </g>
    );
};


// --- COMPONENTS ---

const StatCard = ({ icon: Icon, title, value, valueColor, iconColor, variant = "default" }) => {
    // Layout đặc biệt cho thẻ thứ 4 (Căn giữa, số ở trên, chữ ở dưới, không icon)
    if (variant === "centered") {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col items-center justify-center transition-colors duration-200 h-full min-h-[120px]">
                <h3 className={`text-4xl md:text-5xl mb-2 ${valueColor}`}>{value}</h3>
                <p className="text-sm text-gray-800 dark:text-gray-300">{title}</p>
            </div>
        );
    }

    // Layout chuẩn cho 3 thẻ đầu (Icon bên trái, chữ bên phải)
    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-100 dark:border-gray-700 flex items-center justify-between transition-colors duration-200 h-full min-h-[120px]">
            <div>
                {/* Đã bỏ background tròn, thiết lập kích thước và độ mỏng của nét vẽ icon */}
                {Icon && <Icon size={44} strokeWidth={1.5} className={iconColor} />}
            </div>
            <div className="text-right flex flex-col justify-between h-full">
                <p className="text-sm text-gray-800 dark:text-gray-300 mb-2">{title}</p>
                <h3 className={`text-4xl ${valueColor}`}>{value}</h3>
            </div>
        </div>
    );
};

const AssetDashboard = () => {
    return (
        <Layout>
            <div className="flex justify-between items-center mb-6 border-b border-gray-200 dark:border-gray-800 pb-2">
                <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">Dashboard Tài sản</h1>
            </div>

            {/* ROW 1: STAT CARDS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                <StatCard
                    icon={MonitorSmartphone}
                    title="Tổng tài sản đang có"
                    value="2,354,346"
                    valueColor="text-[#2db2ff] dark:text-sky-400" // Màu xanh dương nhạt cho số
                    iconColor="text-blue-700 dark:text-blue-500" // Màu xanh đậm cho icon
                />
                <StatCard
                    icon={Database}
                    title="Tổng tài sản tồn kho"
                    value="2,354,206"
                    valueColor="text-[#2db2ff] dark:text-sky-400"
                    iconColor="text-blue-700 dark:text-blue-500"
                />
                <StatCard
                    icon={Settings}
                    title="Tổng tài sản đã báo hỏng/sửa chữa"
                    value="630"
                    valueColor="text-orange-500 dark:text-orange-400"
                    iconColor="text-orange-500 dark:text-orange-400"
                />
                <StatCard
                    title="Tổng tài sản đã báo hủy/mất"
                    value="0"
                    valueColor="text-red-600 dark:text-red-500"
                    variant="centered" // Kích hoạt layout đặc biệt cho thẻ số 4
                />
            </div>

            {/* ROW 2: CHARTS */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">

                {/* Cột trái: Chart vị trí tài sản */}
                <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-4 flex flex-col">
                    <h4 className="text-center text-sm font-medium text-gray-400 dark:text-gray-500 mb-6 mt-2">Biểu đồ thống kê theo vị trí tài sản</h4>
                    <div className="flex-1 min-h-[400px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={locationData} layout="vertical" margin={{ top: 5, right: 30, left: 60, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#374151" opacity={0.2} />
                                <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                                <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} width={100} />
                                <Tooltip
                                    cursor={{ fill: 'transparent' }}
                                    contentStyle={{ backgroundColor: '#1f2937', borderColor: '#374151', color: '#f3f4f6' }}
                                    itemStyle={{ color: '#f3f4f6' }}
                                />
                                <Bar dataKey="value" barSize={12} radius={[0, 4, 4, 0]}>
                                    {locationData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.fill || '#e5e7eb'} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Cột phải: 2 Charts xếp chồng */}
                <div className="grid grid-cols-1 gap-6">
                    {/* Chart Line Ngày mua */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-4 min-h-[280px] flex flex-col">
                        <h4 className="text-center text-sm font-medium text-gray-400 dark:text-gray-500 mb-4 mt-2">Biểu đồ thống kê số lượng tài sản theo ngày mua</h4>
                        <div className="flex-1 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={dateData} margin={{ top: 5, right: 10, left: -20, bottom: 25 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#374151" opacity={0.2} />
                                    <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#9ca3af' }} angle={-45} textAnchor="end" axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={(value) => value >= 1000 ? `${value / 1000}k` : value} />
                                    <Tooltip
                                        cursor={{ fill: 'transparent' }}
                                        contentStyle={{ backgroundColor: '#1f2937', borderColor: '#374151', color: '#f3f4f6' }}
                                        itemStyle={{ color: '#f3f4f6' }}
                                    />
                                    <Line type="monotone" dataKey="value" stroke="#9ca3af" strokeWidth={1} dot={(props) => {
                                        const { cx, cy, index } = props;
                                        const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
                                        return <circle cx={cx} cy={cy} r={3} fill={colors[index % colors.length]} key={index} />;
                                    }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Chart Bar Phòng ban (ĐÃ ĐƯỢC CẬP NHẬT) */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-4 min-h-[450px] flex flex-col">
                        <h4 className="text-center text-sm font-medium text-gray-400 dark:text-gray-500 mb-6 mt-2">Biểu đồ thống kê theo phòng ban quản lý</h4>
                        <div className="flex-1 w-full min-h-[400px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={departmentData} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#d1d5db" className="dark:stroke-gray-700" opacity={0.5} />

                                    <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 11 }} domain={[0, 250]} />

                                    {/* Sử dụng CustomYAxisTick và mở rộng độ rộng width=200 để chứa chữ */}
                                    <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} width={200} tick={<CustomDeptYAxisTick />} />

                                    {/* Sử dụng CustomTooltip */}
                                    <Tooltip cursor={{ fill: 'rgba(0,0,0,0.05)' }} content={<CustomDeptTooltip />} />

                                    <Bar dataKey="value" barSize={20}>
                                        {departmentData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.fill} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>

            {/* ROW 3: CHARTS NHÓM TÀI SẢN */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-4 flex flex-col min-h-[500px]">
                    <h4 className="text-left text-sm font-medium text-gray-400 dark:text-gray-500 mb-6 mt-2 pl-4">Biểu đồ thống kê số lượng theo nhóm tài sản (Trừ Thanh lý, Mất, Hủy)</h4>
                    <div className="flex-1 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={assetGroupData} layout="vertical" margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#374151" opacity={0.2} />
                                <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} domain={[0, 30]} tickCount={16} />
                                <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#6b7280' }} axisLine={false} tickLine={false} width={120} />
                                <Tooltip
                                    cursor={{ fill: 'transparent' }}
                                    contentStyle={{ backgroundColor: '#1f2937', borderColor: '#374151', color: '#f3f4f6' }}
                                    itemStyle={{ color: '#f3f4f6' }}
                                />
                                <Bar dataKey="value" barSize={8} radius={[0, 4, 4, 0]}>
                                    {assetGroupData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.fill} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

        </Layout>
    );
};

export default AssetDashboard;